import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import useEmblaCarousel from "embla-carousel-react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Section, Eyebrow } from "./ui-kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback quotes, used only if the API is unavailable.
const QUOTES = [
  {
    text: "The team genuinely get to know each person. Our son comes home happy and full of stories about his day.",
    name: "Parent of someone we support",
  },
  {
    text: "It's the first place that's felt right for our daughter. She's grown in confidence in ways we never thought possible.",
    name: "Family member",
  },
  {
    text: "Warm, patient and completely person-centred. We always feel listened to and kept in the loop.",
    name: "Parent and carer",
  },
  {
    text: "Every day is planned around what matters to him. The difference in his independence has been wonderful to see.",
    name: "Carer",
  },
];

export const Testimonials = () => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);
  const [quotes, setQuotes] = useState(QUOTES);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setQuotes(r.data);
      })
      .catch(() => {});
  }, []);

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);
  const scrollTo = useCallback((i) => embla && embla.scrollTo(i), [embla]);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla]);

  return (
    <Section tone="tinted" data-testid="home-testimonials">
      <div className="max-w-2xl">
        <Eyebrow>Kind words</Eyebrow>
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
          What families and carers say
        </h2>
      </div>

      <div
        className="relative mt-10"
        role="region"
        aria-roledescription="carousel"
        aria-label="Testimonials from families and carers"
      >
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {quotes.map((q, i) => (
              <div
                key={q.id || i}
                className="min-w-0 shrink-0 grow-0 basis-full md:basis-1/2 pl-4 first:pl-0"
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} of ${quotes.length}`}
                data-testid={`testimonial-${i}`}
              >
                <figure className="h-full rounded-3xl bg-white border border-stone-200 p-8 shadow-sm">
                  <Quote className="h-9 w-9 text-orange-500" aria-hidden="true" />
                  <blockquote className="mt-4 text-lg text-stone-800 leading-relaxed">
                    "{q.text}"
                  </blockquote>
                  <figcaption className="mt-5 font-heading font-semibold text-stone-700">
                    — {q.name}
                  </figcaption>
                </figure>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-4">
          <button
            onClick={scrollPrev}
            aria-label="Previous testimonial"
            data-testid="testimonial-prev"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white border-2 border-stone-300 text-stone-800 transition-colors hover:border-orange-600 hover:bg-orange-50"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            onClick={scrollNext}
            aria-label="Next testimonial"
            data-testid="testimonial-next"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white border-2 border-stone-300 text-stone-800 transition-colors hover:border-orange-600 hover:bg-orange-50"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="ml-2 flex items-center gap-2" role="tablist" aria-label="Choose testimonial">
            {quotes.map((_, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={selected === i}
                aria-label={`Go to testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => scrollTo(i)}
                className={`h-2.5 rounded-full transition-all ${selected === i ? "w-7 bg-orange-600" : "w-2.5 bg-stone-300 hover:bg-stone-400"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};
